import React, { Fragment, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import { useDimensionsRef } from 'hooks/useDimensionsRef/useDimensionsRef';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import { useFulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { useInfiniteScroll } from 'pages/ReportsPage/components/AnalyticsDashboards/useInfiniteScroll';
import {
  ChartScrollContainer,
  ChartLoadMoreRow,
  ChartScrollSentinel,
  EmptyStateContainer,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import {
  ChartLegend,
  ChartLegendItem,
  ChartLegendDot,
  ChartLabelWrapper,
  ChartLabelText,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.styled';

const ROW_HEIGHT = 52;
const BAR_HEIGHT = 24;
const MARGIN_TOP = 10;
const MARGIN_BOTTOM = 20;
const RIGHT_MARGIN = 10;
const VALUE_LABEL_CHAR_PX = 8;
const VALUE_LABEL_OUTSIDE_GAP = 6;
const VALUE_LABEL_GAP = 8;
const INNER_LABEL_PADDING = 6;
const LIGHT_TEXT_COLOR = '#FFFFFF';
const DARK_TEXT_COLOR = '#333333';
const DARK_COLOR_THRESHOLD = 128;
const LABEL_WIDTH = 200;
const LABEL_GAP = 8;

type BarValueLabel = { x: number; anchor: 'start' | 'middle' | 'end'; fill: string };

function estimateValueLabelWidth(value: number): number {
  return String(value).length * VALUE_LABEL_CHAR_PX;
}

function isDarkColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r + g + b) / 3 < DARK_COLOR_THRESHOLD;
}

function getRowValueLabels(
  assignedValue: number,
  calloutValue: number,
  assignedWidth: number,
  calloutWidth: number,
  assignedTextColor: string,
): { assigned: BarValueLabel | null; callout: BarValueLabel | null } {
  const showAssigned = assignedValue > 0;
  const showCallout = calloutValue > 0;
  const assignedTextWidth = estimateValueLabelWidth(assignedValue);
  const calloutTextWidth = estimateValueLabelWidth(calloutValue);
  const assignedFitsInside = assignedWidth >= assignedTextWidth + INNER_LABEL_PADDING * 2;
  const calloutFitsInside = calloutWidth >= calloutTextWidth + INNER_LABEL_PADDING * 2;
  const calloutInside = (!showAssigned || assignedFitsInside) && calloutFitsInside;

  let outsideCursor = assignedWidth + calloutWidth + VALUE_LABEL_OUTSIDE_GAP;

  let assigned: BarValueLabel | null = null;
  if (showAssigned) {
    if (assignedFitsInside) {
      assigned = { x: assignedWidth / 2, anchor: 'middle', fill: assignedTextColor };
    } else {
      assigned = { x: outsideCursor, anchor: 'start', fill: DARK_TEXT_COLOR };
      outsideCursor += assignedTextWidth + VALUE_LABEL_GAP;
    }
  }

  let callout: BarValueLabel | null = null;
  if (showCallout) {
    callout = calloutInside
      ? { x: assignedWidth + calloutWidth / 2, anchor: 'middle', fill: DARK_TEXT_COLOR }
      : { x: outsideCursor, anchor: 'start', fill: DARK_TEXT_COLOR };
  }

  return { assigned, callout };
}

export const CalloutRateChart: React.FC = () => {
  const data = useFulfillmentDashboardStore((s) => s.calloutRateByPosition);
  const page = useFulfillmentDashboardStore((s) => s.calloutRateByPositionPage);
  const totalPages = useFulfillmentDashboardStore((s) => s.calloutRateByPositionTotalPages);
  const loadingMore = useFulfillmentDashboardStore((s) => s.calloutRateByPositionLoadingMore);
  const loadMore = useFulfillmentDashboardStore((s) => s.loadMoreCalloutRateByPosition);
  const palette = useChartColors();
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<string | null>(null);

  useInfiniteScroll({
    rootRef: scrollRootRef,
    sentinelRef,
    hasMore: page < totalPages,
    isLoading: loadingMore,
    onLoadMore: loadMore,
  });

  const assignedColor = palette.chartColors[3] || '#1B2A4A';
  const calloutColor = palette.chartColors[1] || '#4CAF50';
  const assignedTextColor = isDarkColor(assignedColor) ? LIGHT_TEXT_COLOR : DARK_TEXT_COLOR;

  const marginLeft = LABEL_WIDTH;

  const height = data ? MARGIN_TOP + MARGIN_BOTTOM + data.length * ROW_HEIGHT : 100;
  const innerWidth = Math.max(containerWidth - marginLeft - RIGHT_MARGIN, 0);

  const xScale = useMemo(() => {
    if (!data) return null;
    const maxVal = d3.max(data, (d) => d.assignedShifts + d.calloutShifts) || 1;
    return d3
      .scaleLinear()
      .domain([0, maxVal * 1.1])
      .range([0, innerWidth]);
  }, [data, innerWidth]);

  if (!data || data.length === 0) {
    return (
      <div ref={containerRef} style={{ width: '100%' }}>
        <EmptyStateContainer>No data available</EmptyStateContainer>
      </div>
    );
  }

  return (
    <Fragment>
      <ChartLegend>
        <ChartLegendItem>
          <ChartLegendDot $color={assignedColor} />
          <span>Assigned Shifts</span>
        </ChartLegendItem>
        <ChartLegendItem>
          <ChartLegendDot $color={calloutColor} />
          <span>Call Out Shifts</span>
        </ChartLegendItem>
      </ChartLegend>
      <ChartScrollContainer ref={scrollRootRef}>
        <div ref={containerRef} style={{ width: '100%', minHeight: 160 }}>
          {containerWidth > 0 && xScale && (
            <svg
              width={containerWidth}
              height={height}
              aria-label="Call out rate by position stacked bar chart"
            >
              <g transform={`translate(${marginLeft},${MARGIN_TOP})`}>
                {data.map((d, index) => {
                  const rowY = index * ROW_HEIGHT;
                  const barHeight = BAR_HEIGHT;
                  const barY = rowY + (ROW_HEIGHT - barHeight) / 2;
                  const assignedWidth = xScale(d.assignedShifts);
                  const calloutWidth = xScale(d.calloutShifts);
                  const { assigned: assignedLabel, callout: calloutLabel } = getRowValueLabels(
                    d.assignedShifts,
                    d.calloutShifts,
                    assignedWidth,
                    calloutWidth,
                    assignedTextColor,
                  );

                  const showAssigned = d.assignedShifts > 0;
                  const showCallout = d.calloutShifts > 0;
                  const rowA11yProps = {
                    role: 'img' as const,
                    'aria-label': `${d.position} assigned: ${d.assignedShifts}, call out: ${d.calloutShifts}`,
                    tabIndex: 0,
                  };

                  const assignedRect = showAssigned && (
                    <rect
                      x={0}
                      y={barY}
                      width={assignedWidth}
                      height={barHeight}
                      fill={assignedColor}
                      rx={3}
                      opacity={hovered === d.position ? 0.85 : 1}
                      {...rowA11yProps}
                    />
                  );
                  const calloutRect = showCallout && (
                    <rect
                      x={assignedWidth}
                      y={barY}
                      width={calloutWidth}
                      height={barHeight}
                      fill={calloutColor}
                      rx={3}
                      opacity={hovered === d.position ? 0.85 : 1}
                      {...(showAssigned ? {} : rowA11yProps)}
                    />
                  );

                  return (
                    <g
                      key={d.position}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        setHovered(d.position);
                        tooltip.show(
                          e,
                          `<strong>${d.position}</strong><br/>Assigned Shifts: ${d.assignedShifts}<br/>Call Out Shifts: ${d.calloutShifts}`,
                        );
                      }}
                      onMouseMove={(e) => tooltip.move(e)}
                      onMouseLeave={() => {
                        setHovered(null);
                        tooltip.hide();
                      }}
                    >
                      <foreignObject
                        x={-marginLeft}
                        y={rowY}
                        width={marginLeft - LABEL_GAP}
                        height={ROW_HEIGHT}
                      >
                        <ChartLabelWrapper>
                          <ChartLabelText>{d.position}</ChartLabelText>
                        </ChartLabelWrapper>
                      </foreignObject>
                      {assignedRect}
                      {calloutRect}
                      {calloutLabel && (
                        <text
                          x={calloutLabel.x}
                          y={barY + barHeight / 2}
                          textAnchor={calloutLabel.anchor}
                          dominantBaseline="middle"
                          fontSize="12px"
                          fontWeight="600"
                          fill={calloutLabel.fill}
                        >
                          {d.calloutShifts}
                        </text>
                      )}
                      {assignedLabel && (
                        <text
                          x={assignedLabel.x}
                          y={barY + barHeight / 2}
                          textAnchor={assignedLabel.anchor}
                          dominantBaseline="middle"
                          fontSize="12px"
                          fontWeight="600"
                          fill={assignedLabel.fill}
                        >
                          {d.assignedShifts}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            </svg>
          )}
        </div>
        {page < totalPages && <ChartScrollSentinel ref={sentinelRef} aria-hidden />}
        {loadingMore && (
          <ChartLoadMoreRow>
            <BKJLoadingSpinner variant="Default" />
          </ChartLoadMoreRow>
        )}
      </ChartScrollContainer>
    </Fragment>
  );
};
